import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnInit, Output, Renderer2, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { CommentBaseComponent } from "@mypxplat/xplat/features";
import {
  buildCommunityPostPath,
  CommunitiesService,
  computeParticipationGate,
  ContentService,
  EventBusService,
  File,
  ParticipationGate,
  ProductRequirement,
  ProductService,
  UserService,
  WindowService,
  WorkspaceService,
  environment,
  Notification,
  HelperService,
  StorageService,
  StorageKeys,
  NotificationService,
  getMyReactions,
  isEmojiOnlyMessage,
  normalizeReactions,
  ReactionType,
  toggleReaction,
  sanitizeAccentColor,
} from "@mypxplat/xplat/core";
import { MessageInputComponent } from "../message-input/message-input.component";
import { take, takeUntil } from "rxjs/operators";
import { AppService, AvoService, BetaService, CommunityReactionService, CommunityShareService, HUB_POST_BACK_KEY, WebCommunityService } from "@mypxplat/xplat/web/core";
import { BETA_TO_JIRA_PROJECT, CreateJiraTicketComponent, EditCommentComponent, HtmlOverlayComponent, SearchContentComponent, SimpleSignupComponent } from "../modals";
import { ClipboardService } from "ngx-clipboard";
import { ActivatedRoute, Router } from "@angular/router";
import { WorkspaceUploadComponent } from "../modals/workspace-upload/workspace-upload.component";
import { FileUploader } from "ng2-file-upload";
import { TranslateService } from "@ngx-translate/core";
import { jiraBrowseUrlFromTicketKey, isValidJiraIssueKey, normalizeJiraIssueKey } from "../../../utils/beta-jira-browse-url";
import { ReactionChangeEvent } from "../reaction-control/reaction-control.component";
import { triggerFileDownload } from "../../../utils/download-file";
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-comment",
  templateUrl: "comment.component.html",
  standalone: false,
})
export class CommentComponent extends CommentBaseComponent implements OnInit, AfterViewInit {
  @Input() betaStringId: string;
  /**
   * Community this post belongs to. Defaults to Fender Studio Pro. Task #35 uses this to
   * build community-aware share URLs; for now it's just accepted so parent templates can
   * bind it.
   */
  @Input() communityStringId: string = "fender-studio-pro";
  @Input() isCommunityPost: boolean;
  /**
   * Lead the post's pills with its community (name and accent dot), as the feed's list rows
   * do — for the post detail, and feeds that span communities. Off elsewhere, where the page
   * already says which community this is.
   */
  @Input() set showCommunityTag(value: boolean) {
    this._showCommunityTag = !!value;
    this._syncCommunityTag();
  }
  private _showCommunityTag = false;
  /** The community pill, resolved once: null hides it. */
  public communityTag: { title: string; accent: string | null } | null = null;
  @Input() index: number;
  @Input() reviewing: boolean;
  @Input() expandRepliesByDefault: boolean = false;
  /** Start with every reply showing instead of the last two ("Collapse replies" still works). Set by the post detail in list view. */
  @Input() showAllRepliesByDefault: boolean = false;

  /**
   * When the reader last opened this thread, from the post detail: `null` = unknown yet (and
   * never set outside the detail), else `{ threadId, since }` with `since` 0 for a first
   * visit. Drives the "New replies" marker and where the detail opens — see
   * `_placeNewRepliesMarker`. Only honoured while `threadId` matches the post on screen: on a
   * switch the post and replies inputs land before this one does.
   */
  @Input() set newRepliesState(value: { threadId: string; since: number } | null) {
    this._newRepliesState = value;
    // A new object is a new visit — that is what re-arms the open-position scroll, even when
    // it's the same thread as last time (A → B → A).
    this._scrolledForThread = null;
    this._placeNewRepliesMarker();
  }
  get newRepliesState(): { threadId: string; since: number } | null {
    return this._newRepliesState;
  }
  private _newRepliesState: { threadId: string; since: number } | null = null;

  /** `since` for the post on screen, or null when unknown or it belongs to another thread. */
  private get _newRepliesSince(): number | null {
    const state = this._newRepliesState;
    const threadId = this._comment?.id || this._comment?.key;
    return state && threadId && String(state.threadId) === String(threadId) ? state.since : null;
  }
  /** The reply the "New replies" divider sits above; null for none. */
  public firstNewReplyId: string | null = null;
  /** The thread the open-position scroll has already run for this visit; cleared when a visit starts. */
  private _scrolledForThread: string | null = null;
  @Input() forceDark: boolean = false;
  @Input() sendOnEnter: boolean = false;
  @Input() queued: boolean = false;
  @Input() displayedSection: any;
  @Input() scheudled: boolean = false;
  /** Rendered in a beta community's Announcements section — the post is shown in full there, so "View Post" is hidden. */
  @Input() isAnnouncement: boolean = false;
  @Output() replied: EventEmitter<any> = new EventEmitter();
  @Output() flagged: EventEmitter<any> = new EventEmitter();
  @Output() unflagged: EventEmitter<any> = new EventEmitter();
  @Output() liked: EventEmitter<any> = new EventEmitter();
  @Output() flagCleared: EventEmitter<any> = new EventEmitter();
  @Output() deleteIndexSet: EventEmitter<any> = new EventEmitter();
  @Output() channelSelected: EventEmitter<any> = new EventEmitter();
  @Output() topicSelected: EventEmitter<any> = new EventEmitter();
  @Output() postEdited: EventEmitter<any> = new EventEmitter();
  @Output() editMessageClicked: EventEmitter<any> = new EventEmitter();
  @Output() deleteMessageClicked: EventEmitter<any> = new EventEmitter();
  @Output() adminDeleteMessageClicked: EventEmitter<any> = new EventEmitter();
  @ViewChild("replyInput", { static: false }) replyInput: MessageInputComponent;
  public replyingToReply: any;
  /** The banner's snippet of `replyingToReply`, set with it — a field, so the template runs no method. */
  public replyingToSnippet = "";
  public replyingToComment: any;
  public showingAllReplies: boolean = false;
  public shouldShowLikesList: any = false;
  public likesContainerHovering: boolean = false;

  public pinReplyingToPost: boolean = true;
  /** Include the member's community signature on the reply being written. Resets after each reply. */
  public replySignatureEnabled: boolean = true;

  public uploadingFile: any = false;
  public uploadingFileProgress: number;

  public uploadingFiles: any = false;
  public uploadedAttachments: Array<File>;

  public uploader: FileUploader;
  public hasBaseDropZoneOver: any;

  public articleMap: any = {};

  public betaManagerEditingChannels: boolean = false;
  public shouldShowChannelSelector: boolean;

  @Input()
  get replies(): Array<any> {
    return this._replies;
  }
  set replies(replies: Array<any>) {
    this._replies = replies;
    this.buildReplies();
  }
  private _replies = [];

  @Input()
  get comment(): any {
    return this._comment;
  }
  set comment(comment: any) {
    // A reused detail pane swapping to another post starts that post expanded again.
    if (this.showAllRepliesByDefault && comment?.id !== this._comment?.id) this.showingAllReplies = true;
    this._comment = comment;
    this.hasSubject = typeof comment?.subject === "string" && comment.subject.trim().length > 0;
    this.hydrateCommentReactionState(this._comment);
    this._hydrateAuthorAvatar(this._comment?.author);
    this.syncSavedBetaReviewJiraBrowseUrl();
    this.computePostDetailLink();
  }
  private _comment: any = {};

  /** Drives the subject-first header layout. Set in the `comment` setter — not a getter — so the template reads a plain field. */
  public hasSubject = false;

  /**
   * Precomputed `[routerLink]` and `[queryParams]` for the timestamp anchor. Both are
   * plain fields (not getters) so template bindings don't re-evaluate them each change-
   * detection cycle. Kept in sync with `this._comment` / `this.betaStringId` /
   * `this.communityStringId` via `computePostDetailLink()` — called from the `set comment`
   * setter AND `ngOnInit` since @Input evaluation order isn't guaranteed and either the
   * comment or the community context may land first.
   *
   * Wave 1 of docs/community-seo-plan.md — timestamp anchor is the primary crawler-
   * discovery path from a prerendered feed page into individual post-detail URLs.
   */
  public postDetailLink: any[] | string = "";
  public postDetailQueryParams: any = null;
  public removeEventListener: () => void;

  public isBetaManager: boolean = false;
  public isAdmin: boolean = false;
  public markingAsReviewed: boolean = false;
  public betaReviewDetails: any = {
    reviewed_by: {},
    reviewed_at: new Date(),
    review_text: "",
    jira_ticket_number: "",
    jira_ticket_link: "",
  };
  public isPostDetail: boolean = false;
  public reviewedOnDate: Date;
  public savedBetaReviewJiraBrowseUrl: string | null = null;
  public betaManagerNotificationPreferences: any;

  /**
   * Participation gate for the reply flow — computed via the shared
   * `computeParticipationGate` helper (see `community.model.ts`). Same rule table as
   * `<myp-create-post-input>.postGate` so posting and replying stay in lockstep. Read by
   * `toggleReplyUI()` to decide whether to open the reply input or show an upgrade prompt.
   */
  public replyGate: ParticipationGate = { ok: false, reason: null };
  /**
   * Whether this reader may react here. Derived from `replyGate` — see `updateReactionGate`.
   * A property rather than a getter so it can be read from a binding without re-running on
   * every change-detection pass.
   */
  public canReact = false;

  /**
   * Palette used for the initials-fallback avatar when a comment author has no photo.
   * Colors are Fender-flavored — the same accent hues we use elsewhere in-app plus a
   * few complementary tones so a user with common initials doesn't collide with the
   * next post above them. Order matters: `avatarColorFor()` picks by hashing the
   * author id modulo palette length, so this ordering IS load-bearing — reshuffling
   * changes which color a specific user gets.
   */
  private static readonly AVATAR_COLOR_PALETTE: readonly string[] = [
    "#d52b1e", // fender red
    "#ff7a1a", // amp orange
    "#f0c419", // pickup gold
    "#4bb543", // stage green
    "#1f7a8c", // teal
    "#3b6ea8", // blues blue
    "#7c4bd0", // psych purple
    "#c93b8e", // magenta
    "#8a5a44", // rosewood
    "#5c5c5c", // slate
  ];

  /**
   * Hydrate `author._initials` and `author._avatarColor` in place. Idempotent — skips
   * work if the fields are already set. Called from the `set comment` setter for the
   * parent post's author and from `buildReplies()` for every reply's author, so the
   * template can read the values as plain properties (no method calls in bindings,
   * which the pre-commit hook enforces per the project's perf rule).
   *
   * Fields:
   *  - `_initials`: first + last name initials uppercased; falls back to "?" on empty.
   *  - `_avatarColor`: deterministic pick from AVATAR_COLOR_PALETTE keyed off id, so
   *    the same user always gets the same tint across sessions and re-renders.
   */
  private _hydrateAuthorAvatar(author: any): void {
    if (!author || author._initials) return;
    const parts = String(author.name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) author._initials = "?";
    else if (parts.length === 1) author._initials = parts[0].charAt(0).toUpperCase();
    else author._initials = (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();

    const id = String(author.id ?? "");
    if (!id) {
      author._avatarColor = "#5c5c5c";
      return;
    }
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    }
    const palette = CommentComponent.AVATAR_COLOR_PALETTE;
    author._avatarColor = palette[hash % palette.length];
  }

  constructor(
    userService: UserService,
    private _win: WindowService,
    private _zone: NgZone,
    private _eventbusService: EventBusService,
    public contentService: ContentService,
    public appService: AppService,
    public communityService: WebCommunityService,
    public workspaceService: WorkspaceService,
    private _clipboardService: ClipboardService,
    public router: Router,
    public changeDetector: ChangeDetectorRef,
    public avoService: AvoService,
    private communityReactionService: CommunityReactionService,
    private _shareService: CommunityShareService,
    public renderer: Renderer2,
    private elementRef: ElementRef,
    public helperService: HelperService,
    private notificationService: NotificationService,
    public betaService: BetaService,
    public storageService: StorageService,
    public activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private communitiesService: CommunitiesService,
    private productService: ProductService
  ) {
    super(userService);
  }

  private _syncCommunityTag() {
    const id = this.comment?.community_string_id || this.communityStringId;
    const community = this._showCommunityTag && this.isCommunityPost && !this.betaStringId && !this.comment?.parentID ? this.communitiesService.getCommunity(id) : null;
    this.communityTag = community ? { title: community.title, accent: sanitizeAccentColor(community.branding?.accent_color) } : null;
  }

  ngOnInit() {
    this._syncCommunityTag();
    if (this.showAllRepliesByDefault) this.showingAllReplies = true;
    // inspect the url to see if they on post detail
    this.isPostDetail = this.router.url.includes("/community/post/") || this.router.url.includes("/beta/betapost");
    // Recompute — the comment @Input setter may have fired before betaStringId /
    // communityStringId were bound (Angular doesn't guarantee ordering across inputs).
    this.computePostDetailLink();
    this.isBetaManager = this.user?.groups?.length ? this.user.groups.some((group: any) => group.name === "beta_manager") : false;
    this.isAdmin = this.user?.groups?.length ? this.user.groups.some((group: any) => group.name === "admin") : false;
    super.ngOnInit();

    // Beta has no reply gate wired below (membership is its own gate), so seed the reaction
    // gate here — otherwise it would sit at its `false` default and swallow beta reactions.
    this.updateReactionGate();

    // A save toggled from the feed's list row lands on the row's copy of the post; keep the
    // bookmark here in step when this is a different object.
    this.communityService.postPinsChanged$.pipe(takeUntil(this.destroy$)).subscribe(({ id, pins }) => {
      if (this.comment && this.comment.id === id && this.comment.pins !== pins) this.comment.pins = pins;
    });

    // Reply-gate wiring. Same shape as `<myp-create-post-input>`: participation_condition
    // + user state + subDetails, recomputed reactively so an in-session upgrade unlocks
    // the reply UI without a reload. Beta bypasses via the `betaStringId` short-circuit in
    // `toggleReplyUI`.
    if (!this.betaStringId) {
      this.communitiesService.communities$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateReplyGate());
      this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateReplyGate());
      this.userService.subDetails$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateReplyGate());
      this.productService.products$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateReplyGate());
      this.updateReplyGate();

      // Same fail-open on subscription-details as the compose form — fetch once so the
      // gate can resolve Fender Play state definitively on first mount.
      if (!this.userService.subDetails && this.userService.user) {
        this.userService.getSubscriptionDetails().pipe(takeUntil(this.destroy$)).subscribe();
      }
    }
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    this.communityService.buildHtmlComment(this.comment);
    this.hydrateCommentReactionState(this.comment);

    // Apply search query highlights to the built HTML message
    if (this.comment._searchQuery && this.comment.htmlMessage) {
      const escaped = this.comment._searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "gi");
      this.comment.htmlMessage = this.comment.htmlMessage.replace(/(<[^>]*>)|([^<]+)/g, (match, tag, text) => {
        if (tag) return tag;
        return text.replace(regex, "<span class='algolia-highlight'>$1</span>");
      });
    }

    if (this.comment.file && this.workspaceService.audioFiles[this.comment.file.extension] && this.comment.file.filesize < 10000000) {
      this.comment.shouldLoadWaveform = true;
    }
    this.removeEventListener = this.renderer.listen(this.elementRef.nativeElement, "click", (event) => {
      this.handleAnchorClick(event);
    });
    if (this.comment.articles) {
      this.comment.articles.forEach((item) => {
        this.articleMap[item.article.id] = item;
      });
    }
    if (this.comment.beta_review_details) {
      this.betaReviewDetails = {
        reviewed_by: { ...this.comment.beta_review_details.reviewed_by },
        reviewed_on: this.comment.beta_review_details.reviewed_on,
        review_text: this.comment.beta_review_details.review_text,
        jira_ticket_number: this.comment.beta_review_details.jira_ticket_number,
        jira_ticket_link: this.comment.beta_review_details.jira_ticket_link,
      };
      if (this.comment.beta_review_details.reviewed_on?.toDate) {
        this.reviewedOnDate = this.comment.beta_review_details.reviewed_on.toDate();
      }
    }
    if (this.betaStringId && !this.comment.beta_status) this.comment.beta_status = "none";
    if (this.betaStringId && !this.comment.beta_type) this.comment.beta_type = "none";

    if (this.betaStringId) {
      this.pinReplyingToPost = false;
      this.betaService.getBetaNotificationManagerPreferences(this.betaStringId).subscribe((result: any) => {
        this.betaManagerNotificationPreferences = result;
      });
    }
  }

  ngAfterViewInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const replyId = params["reply"];
      if (replyId) {
        setTimeout(() => {
          const repliesIds = this.replies.map((reply) => reply.id);
          if (repliesIds.includes(replyId)) {
            if (!document.getElementById(replyId)) {
              this.showingAllReplies = true;
              this.changeDetector.detectChanges();
            }

            setTimeout(() => {
              const element = document.getElementById(replyId);

              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });

                element.style.boxShadow = "0 0 0 3px var(--primary-brand-color)";
                element.style.borderRadius = "8px";
              }
            }, 100);
          }
        }, 1000);
      }
    });
  }

  /**
   * The community's `mentions_enabled` flag. Mirrors the composer: the admin
   * switch now actually gates the reply editor too. Fails open.
   */
  get mentionsEnabled(): boolean {
    return this.communitiesService.getCommunity(this.communityStringId)?.features?.mentions_enabled !== false;
  }

  handleAnchorClick(event) {
    if (event.target && event.target.dataset && event.target.dataset.mentionid) {
      if (event.target.dataset.mentionid != "studio_one_experts") this.viewProfile(event.target.dataset.mentionid);
    } else if (event.target && event.target.dataset && event.target.dataset.articleid) {
      this.showArticle(this.articleMap[event.target.dataset.articleid]);
    } else if (event.target && event.target.dataset && event.target.dataset.sectionid && event.target.dataset.categoryid) {
      let queryParams = { category: event.target.dataset.categoryid, section: event.target.dataset.sectionid };
      this.router.navigate(["support/articles"], { queryParams: queryParams });
    } else if (event.target && event.target.dataset && event.target.dataset.categoryid) {
      let queryParams = { category: event.target.dataset.categoryid };
      this.router.navigate(["support/articles"], { queryParams: queryParams });
    } else if (event.target && event.target.dataset && event.target.dataset.videoid) {
      this.watchVideo(event.target.dataset.videoid);
    } else if (event.target && event.target.dataset && event.target.dataset.lessonid) {
      this.router.navigate(["learn/lesson/" + event.target.dataset.lessonid]);
    } else if (event.target && event.target.dataset && event.target.dataset.courseid) {
      this.router.navigate(["learn/course/" + event.target.dataset.courseid]);
    } else if (event.target && event.target.dataset && event.target.dataset.hash) {
      this.router.navigate(["/share/" + event.target.dataset.hash]);
    } else if (event.target && event.target.dataset && event.target.dataset.communitypath) {
      // Imported Discord posts link to each other. The importer rewrote in-guild
      // discord.com permalinks to community paths (paeapi scripts/lib/discord-markdown.js);
      // routing here keeps them in-app instead of a full page load. Only same-origin
      // community paths are honoured — the attribute is author-controlled content.
      const path = event.target.dataset.communitypath;
      if (path.startsWith("/community/")) this.router.navigateByUrl(path);
    }
  }

  showAttach(type) {
    // Scrollable: the results scroll inside the body between a fixed header and footer, the
    // layout that scrolls reliably by touch on phones (the whole-content scroller didn't).
    const modalRef = this.appService.showModal(SearchContentComponent, { size: "lg", scrollable: true });
    modalRef.componentInstance.type = type;
    modalRef.componentInstance.contentSelected.pipe(take(1)).subscribe((result) => {
      if (type == "articles") {
        this.replyInput.selectedArticlesInMessage.push({ selector: false, editorSelector: false, article: result });
      } else {
        this.replyInput.selectedLearnContentInMessage.push({ selector: false, editorSelector: false, content: result });
      }
    });
  }

  /**
   * Timestamp-anchor click handler. Primes the back-navigation cache so `<myp-community-
   * post-detail>`'s back button returns to the exact feed / channel the user was viewing;
   * the actual navigation is driven by `[routerLink]` on the anchor, so we don't call
   * `router.navigate()` here anymore.
   *
   * Previously employee-gated on the non-beta branch — that gate was dropped 2026-08-31
   * (docs/community-seo-plan.md) so ANY user (or crawler) can follow the timestamp anchor
   * into post detail. Post-detail is a rarely-visited surface for real users, but it needs
   * to be reachable via a real `<a href>` so search engines can index individual posts.
   */
  detailClick() {
    // Inside the Communities hub: back returns to the hub as it was. The community page's list
    // cache below is built from the service's last community feed, which on the hub is some
    // earlier page's — restoring it showed a stale list (a just-made post missing from it).
    if (!this.betaStringId && this.router.url.startsWith("/communities")) {
      this.communityService.communityListBackCache = {
        key: HUB_POST_BACK_KEY,
        activeParentPosts: [],
        activeParentThreadMap: {},
        lastVisiblePost: null,
        activePostsLimitReached: false,
        returnUrl: this.router.url,
        viewedPostId: this.comment?.id,
      };
      return;
    }
    if (this.betaStringId) {
      this.communityService.saveCommunityListBackCache(this.betaStringId, this.communityService.selectedChannel);
    } else {
      this.communityService.saveCommunityListBackCache(undefined, this.communityService.selectedChannel);
    }
    this.communityService.enrichCommunityListBackCacheForPostNavigation(this.comment.id);
  }

  /**
   * Recomputes `postDetailLink` + `postDetailQueryParams` from the current comment /
   * community context. Called from the `set comment` setter and `ngOnInit` — see the
   * field docs above for why both. Beta posts route to `/beta/betapost/{id}?betaStringId=X`;
   * non-beta posts route to the canonical `/community/{stringId}/post/{id}`. Anchor text
   * is the timestamp; users clicking navigate to post detail, crawlers follow the href.
   */
  private computePostDetailLink() {
    if (!this._comment?.id) {
      this.postDetailLink = "";
      this.postDetailQueryParams = null;
      return;
    }
    if (this.betaStringId) {
      this.postDetailLink = ["/beta/betapost", this._comment.id];
      this.postDetailQueryParams = { betaStringId: this.betaStringId };
      return;
    }
    // Prefer the post's own `community_string_id` (source of truth after the DENG-725 backfill)
    // and fall back to the route-bound input for pre-backfill edge cases.
    const stringId = this._comment.community_string_id || this.communityStringId || "fender-studio-pro";
    this.postDetailLink = ["/community", stringId, "post", this._comment.id];
    this.postDetailQueryParams = null;
  }

  removeArticle(article, e) {
    this.replyInput.removeSelectedArticle(article);
    e.stopPropagation();
  }

  showArticle(article) {
    let modalRef = this.appService.showModal(HtmlOverlayComponent, { size: "lg" });
    modalRef.componentInstance.title = article.article.name;
    modalRef.componentInstance.html = '<style type="text/css">img { max-width: 100%; }</style>' + article.article.body;
  }

  goToPublicFile(item) {
    this.router.navigate(["/share/" + item.publicFile.hash]);
  }

  removeLearnContent(item, e) {
    this.replyInput.removeSelectedLearnContent(item);
    e.stopPropagation();
  }

  removePublicFile(item, e) {
    this.replyInput.removePublicFile(item);
    e.stopPropagation();
  }

  removeEmbed(embed, e) {
    this.replyInput.removeEmbed(embed);
    e.stopPropagation();
  }

  deletePinnedPost() {
    this.appService.confirm(this.translate.instant("com.confirm_remove_pinned_post")).then((result) => {
      if (result) {
        this.communityService.deletePost(this.comment.id, false, "beta_community_posts").then(() => {
          this._eventbusService.emit(this._eventbusService.types.betaAnnouncementUpdated, true);
        });
      }
    });
  }

  /** Pinned announcements are read-only unless a beta manager / admin opted them into comments. */
  get commentsAllowedOnPinned(): boolean {
    return !!this.comment?.allow_comments;
  }

  /** True when the post is a pinned announcement with comments still closed — hides the action row. */
  get pinnedAndLocked(): boolean {
    return !!this.comment?.pinned && !this.commentsAllowedOnPinned;
  }

  get canToggleAnnouncementComments(): boolean {
    return !!this.betaStringId && !!this.comment?.pinned && (this.isBetaManager || this.isAdmin);
  }

  toggleAnnouncementComments() {
    if (!this.canToggleAnnouncementComments) return;

    const allow = !this.commentsAllowedOnPinned;
    const confirmMessage = allow
      ? "Are you sure you want to enable comments on this pinned post? Beta participants will be able to reply and react to it."
      : "Are you sure you want to disable comments on this pinned post? Existing replies are kept but hidden, and no new replies can be added.";
    this.appService.confirm(confirmMessage).then((result) => {
      if (!result) return;
      this.communityService
        .setAnnouncementCommentsAllowed(this.comment, allow, "beta_community_posts")
        .then(() => {
          this.comment.allow_comments = allow;
          this._eventbusService.emit(this._eventbusService.types.betaAnnouncementUpdated, true);
        })
        .catch((error) => {
          console.error("Error updating comment settings:", error);
          this.appService.alert(error.message || "An error occurred while updating the comment settings.");
        });
    });
  }

  unpinBetaPost() {
    this.appService.confirm("Are you sure you want to unpin this post? This will place the post in the feed in the order it was created.").then((result) => {
      if (result) {
        this.communityService.updatePost(this.comment.id, { pinned: false }, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection).then(() => {
          this._eventbusService.emit(this._eventbusService.types.betaAnnouncementUpdated, true);
        });
      }
    });
  }

  adminPinUnpinPost() {
    if (!this.isBetaManager) {
      return;
    }

    const action = this.comment.pinned ? "unpin" : "pin";
    this.appService.confirm(this.translate.instant("com.confirm_pin_unpin_post", { action })).then((result) => {
      if (result) {
        this.communityService
          .adminPinUnpinPost(this.comment, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection)
          .then(() => {
            this._eventbusService.emit(this._eventbusService.types.betaAnnouncementUpdated, true);
          })
          .catch((error) => {
            console.error("Error pinning/unpinning post:", error);
            this.appService.alert(error.message || "An error occurred while pinning/unpinning the post.");
          });
      }
    });
  }

  buildReplies() {
    if (this.replies) {
      if (this.expandRepliesByDefault && this.replies.length) this.comment.replying = true;
      if (this.replies.length) {
        this.replies.sort((a, b) => {
          return a.created < b.created ? -1 : 1;
        });
      }
      this.replies.forEach((reply) => {
        this.hydrateCommentReactionState(reply);
        this._hydrateAuthorAvatar(reply.author);
        if (!reply.htmlMessage) {
          this.communityService.buildHtmlComment(reply);
        }
        reply.isEmojiOnly = isEmojiOnlyMessage(reply.htmlMessage || reply.message);
        if (this.comment._searchQuery && reply.htmlMessage) {
          const escaped = this.comment._searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(`(${escaped})`, "gi");
          reply.htmlMessage = reply.htmlMessage.replace(/(<[^>]*>)|([^<]+)/g, (match, tag, text) => {
            if (tag) return tag;
            return text.replace(regex, "<span class='algolia-highlight'>$1</span>");
          });
        }
        if (reply.articles) {
          reply.articles.forEach((item) => {
            this.articleMap[item.article.id] = item;
          });
        }
      });
      // After the loop above, so every target's author avatar is already hydrated.
      this.replies.forEach((reply) => this._hydrateReplyTarget(reply));
    }
    this._placeNewRepliesMarker();
  }

  /** Milliseconds for a Firestore Timestamp, a Date, or an epoch number. */
  private _millis(value: any): number {
    if (!value) return 0;
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (value instanceof Date) return value.getTime();
    return Number(value) || 0;
  }

  /**
   * Where the post detail opens, and where "New replies" goes.
   *
   * - first visit (`0`): no marker, stay at the top — the post is what's new.
   * - seen before: the marker goes above the first reply newer than your last visit (your
   *   own replies don't count), and the view scrolls to it.
   * - seen, nothing new since: no marker; scroll to the latest reply.
   *
   * The marker is recomputed as replies arrive; the scroll happens once per visit, when both
   * the last-visit time and the replies are in. A `?reply=` deep link (notifications) does
   * its own scrolling in `ngAfterViewInit` and wins.
   */
  private _placeNewRepliesMarker() {
    const since = this._newRepliesSince;
    const replies = this.replies || [];
    const me = this.userService.user?.id;
    let first: any = null;
    if (since) {
      first = replies.find((r) => this._millis(r.created) > since && `${r.author?.id}` !== `${me}`) || null;
    }
    this.firstNewReplyId = first ? first.id || first.key : null;

    const threadId = this._comment?.id || this._comment?.key;
    if (since === null || !threadId || this._scrolledForThread === threadId || !this.replies) return;
    this._scrolledForThread = threadId;
    if (!since || !replies.length || this.activatedRoute.snapshot.queryParams["reply"]) return;

    // Collapsed threads only render the last two replies.
    if (first && replies.indexOf(first) < replies.length - 2) this.showingAllReplies = true;
    this._holdOpenPosition(first ? "new" : "bottom");
  }

  /**
   * Put the detail at "New replies" or at the bottom of the thread — instantly, not smooth:
   * a smooth scroll through a long thread is distracting and can be cut short by the layout
   * still moving under it.
   *
   * Re-applied a few times over the first second, because the thread keeps growing after
   * the first paint (the live replies watcher replaces the cached copy, images and embeds
   * load), and a single early jump lands short. Stops the moment the reader scrolls, clicks
   * or types, so it never fights them.
   *
   * Scrolls the split pane when inside one (`.community-split-pane` is its own scroll
   * container), else the window, leaving 90px for the fixed nav.
   */
  private _holdOpenPosition(mode: "new" | "bottom") {
    const threadKey = this._comment?.key;
    // This component's own host, NOT `getElementById(post id)`: in split view the feed's list
    // row carries the same id and comes first in the DOM, so the lookup found the row and
    // scrolled the window instead of the pane.
    const card = this.elementRef?.nativeElement as HTMLElement | undefined;
    if (!card) return;
    const pane = card.closest(".community-split-pane") as HTMLElement | null;
    const scroller: HTMLElement | Window = pane || window;
    let cancelled = false;
    const cancel = () => (cancelled = true);
    const events = ["wheel", "touchstart", "pointerdown", "keydown"];
    events.forEach((e) => scroller.addEventListener(e, cancel, { passive: true, once: true }));

    const apply = () => {
      // Gone, or the pane moved on to another post.
      if (cancelled || this._comment?.key !== threadKey || !card.isConnected) return;
      if (mode === "new") {
        const marker = card.querySelector(".new-replies-divider") as HTMLElement | null;
        if (!marker) return;
        const markerTop = marker.getBoundingClientRect().top;
        if (pane) pane.scrollTop += markerTop - pane.getBoundingClientRect().top - 12;
        else window.scrollTo({ top: window.scrollY + markerTop - 90, behavior: "instant" as ScrollBehavior });
      } else if (pane) {
        pane.scrollTop = pane.scrollHeight - pane.clientHeight;
      } else {
        const bottom = card.getBoundingClientRect().bottom;
        window.scrollTo({ top: Math.max(0, window.scrollY + bottom - window.innerHeight + 24), behavior: "instant" as ScrollBehavior });
      }
    };

    // First pass on the next animation frame: change detection has already rendered the new
    // thread (and the marker), and rAF runs before the browser paints — so when the answer
    // was already in memory, the first frame shown is already in position. The later passes
    // catch the thread growing under it.
    requestAnimationFrame(apply);
    [250, 600, 1200].forEach((ms) => this._win.setTimeout(apply, ms));
    this._win.setTimeout(() => events.forEach((e) => scroller.removeEventListener(e, cancel)), 1300);
  }

  /**
   * Resolves `reply.replyingToID` (the reply it answers) into `reply._replyTarget`, the plain
   * view-model the reference row above the reply renders — no lookups from the template.
   * Only the id is stored on the doc, never a copy of the target: every reply of the post
   * is already in `this.replies`, and a copy would go stale on edits and on the Discord
   * claim reconciler's author rewrites. A target that isn't among this post's replies
   * (deleted, or not ours) renders as "Original reply was deleted".
   */
  private _hydrateReplyTarget(reply: any) {
    const targetId = reply?.replyingToID;
    if (!targetId) {
      reply._replyTarget = null;
      return;
    }
    const target = (this.replies || []).find((r) => (r.id || r.key) === targetId);
    if (!target || target.deleted) {
      reply._replyTarget = { missing: true };
      return;
    }
    reply._replyTarget = {
      id: targetId,
      name: target.author?.name,
      photo: target.author?.photo,
      initials: target.author?._initials,
      color: target.author?._avatarColor,
      snippet: this._replySnippet(target),
    };
  }

  /** One line of a reply's text for the reference row and the "Replying to" banner; CSS does the visual truncation. */
  private _replySnippet(reply: any): string {
    const text = this.helperService
      .stripHtml(reply?.message || "")
      .replace(/\s+/g, " ")
      .trim();
    if (text) return text.length > 140 ? text.substring(0, 140) + "…" : text;
    return reply?.files?.length ? this.translate.instant("com.reply_attachment") : "";
  }

  /** Reference row click: bring the answered reply into view, expanding the list if it's collapsed. */
  scrollToReply(replyId: string) {
    if (!document.getElementById(replyId)) {
      this.showingAllReplies = true;
      this.changeDetector.detectChanges();
    }
    const el = document.getElementById(replyId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Restart the flash if it's clicked again mid-animation.
    el.classList.remove("highlight-flash");
    void el.offsetWidth;
    el.classList.add("highlight-flash");
    this._win.setTimeout(() => el.classList.remove("highlight-flash"), 3000);
  }

  /** The × on the "Replying to" banner: keep the composer open, but as a reply to the post. */
  cancelReplyTarget() {
    this.replyingToReply = undefined;
    this.replyingToSnippet = "";
    this.replyingToComment = this.comment;
    this.changeDetector.detectChanges();
    this.replyInput?.focusMessageInput();
  }

  playAudio(file, commentID, event) {
    if (event) event.stopPropagation();
    if (this.communityService.playingAudioFile && this.communityService.playingAudioFile.id == file.id) {
      this._eventbusService.emit(this._eventbusService.types.playAudio, false);
    } else {
      this._eventbusService.emit(this._eventbusService.types.playAudio, {
        ...file,
        hideDownloadOption: !this.betaStringId,
        routeToSource: this.router.url,
        sourcePostID: commentID,
        triggerElement: event?.target as HTMLElement,
      });
    }
  }

  viewProfile(id) {
    if (!this.isCommunityPost) return;
    if (!this.user) {
      this.appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => this.viewProfile(id),
        () => {}
      );
      return;
    }
    this.router.navigate(["/profile", id]);
  }

  downloadFile(file) {
    triggerFileDownload(file.url, file.filename);
  }

  audioPlayed(args) {
    if (this.communityService.currentlyPlayingWaveformComponent) this.communityService.currentlyPlayingWaveformComponent.toggleAudio();
  }

  /** Refresh the gate from current cached inputs. See `computeParticipationGate`. */
  private updateReplyGate() {
    const user = this.userService.user;
    const community = this.communitiesService.getCommunity(this.communityStringId);

    // Products live in ProductService — hold the gate silently until they've loaded
    // so we don't flash a "you don't own X" alert to someone who actually does.
    // Same pattern as `<myp-create-post-input>.updatePostGate`. Only hold for signed-in
    // users — otherwise `products$` never emits and we'd suppress the "sign in" prompt.
    if (user && community?.participation_condition === "product_owner" && !this.productService.hasGottenProducts) {
      this.replyGate = { ok: false, reason: null };
      this.updateReactionGate();
      if (!this.productService.isGettingProducts) this.productService.getProducts();
      return;
    }

    this.replyGate = computeParticipationGate({
      community,
      user,
      profilePublic: !!this.communityService.profile?.public,
      subDetails: this.userService.subDetails,
      userGroups: Array.isArray(user?.groups) ? user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : undefined,
      ownsProduct: (req: ProductRequirement) => this.productService.ownsProductRequirement(req),
    });
    this.updateReactionGate();
  }

  /**
   * Recomputes `canReact` from the same gate replying uses. Reactions used to be gated on
   * `user.active_subscription` outright, which is Fender Studio Pro's rule and not every
   * community's: in Tone Master Pro (`participation_condition: "none"`) a signed-in member
   * who tapped Like was bounced to the FSP onboarding page.
   *
   * Two deliberate differences from the reply gate:
   *   - `profile_not_public` does NOT block. It's only ever returned once the participation
   *     condition is already met, and a reaction publishes no content the way a reply does —
   *     blocking on it would also newly lock out FSP subscribers with private profiles, who
   *     can react today.
   *   - a null reason with `ok: false` is `updateReplyGate`'s "products still loading" hold.
   *     Replying fails open there rather than accusing an owner of not owning; so does this.
   */
  private updateReactionGate() {
    if (this.betaStringId) {
      // Beta membership is its own gate — the same short-circuit `toggleReplyUI` makes.
      this.canReact = true;
      return;
    }
    this.canReact = this.replyGate.ok || this.replyGate.reason === "profile_not_public" || this.replyGate.reason === null;
  }

  /**
   * Says why a reaction was refused. Mirrors `toggleReplyUI`'s messages minus the
   * profile-public case, which `updateReactionGate` doesn't treat as a blocker.
   */
  private explainReactionBlock() {
    switch (this.replyGate.reason) {
      case "needs_fsp_plus":
        this.upgrade();
        break;
      case "needs_fender_play":
        window.open(environment.fenderPlayUrl, "_blank");
        break;
      case "needs_product_ownership": {
        const community = this.communitiesService.getCommunity(this.communityStringId);
        const names = (community?.requiredProducts || []).map((p) => p.label || p.value).join(", ") || "the required product";
        this.appService.alert(`Register one of the following products with your account to react here: ${names}.`);
        break;
      }
      case "needs_group_membership":
        this.appService.alert("Only members of specific groups can react in this community. Contact a community admin if you believe you should have access.");
        break;
      default:
        break;
    }
  }

  toggleReplyUI(comment, replyingToReply?, forceExpand?) {
    if (this.user) {
      // Beta community keeps its historical rule (public replies bypass all
      // subscription/profile gates — beta membership is its own gate). Every non-beta
      // community routes through `replyGate.canReply`.
      if (!this.betaStringId && !this.replyGate.ok) {
        switch (this.replyGate.reason) {
          case "profile_not_public":
            this.appService.alert("Your profile needs to be set to public in order to reply to public posts.");
            break;
          case "needs_fsp_plus":
            this.upgrade();
            break;
          case "needs_fender_play":
            window.open(environment.fenderPlayUrl, "_blank");
            break;
          case "needs_product_ownership": {
            // No inline card path here (comment lives deep in the thread) — flash the
            // required product list in an alert. The compose form renders a richer
            // "register one of these" prompt with thumbnails; this is the shorter form.
            const community = this.communitiesService.getCommunity(this.communityStringId);
            const names = (community?.requiredProducts || []).map((p) => p.label || p.value).join(", ") || "the required product";
            this.appService.alert(`Register one of the following products with your account to reply here: ${names}.`);
            break;
          }
          case "needs_group_membership":
            this.appService.alert("Only members of specific groups can reply in this community. Contact a community admin if you believe you should have access.");
            break;
          default:
            this.openReplyUI(comment, replyingToReply, forceExpand);
        }
        return;
      }
      this.openReplyUI(comment, replyingToReply, forceExpand);
    } else {
      this.appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => {
          this.toggleReplyUI(comment, replyingToReply, forceExpand);
        },
        (error) => {}
      );
    }
  }

  private openReplyUI(comment, replyingToReply?, forceExpand?) {
    if (replyingToReply) {
      this.replyingToReply = replyingToReply;
      this.replyingToSnippet = this._replySnippet(replyingToReply);
    } else {
      this.replyingToComment = comment;
      this.replyingToReply = undefined;
    }
    if (forceExpand) {
      comment.replying = true;
    } else {
      comment.replying = !comment.replying;
    }
    this.changeDetector.detectChanges();
    if (comment.replying) {
      let inputContainerEl = document.getElementById(comment.key + "_reply_input_container");
      if (inputContainerEl && inputContainerEl.scrollIntoView) {
        // "nearest", not "center": the composer usually opens from the reply prompt, which is
        // already on screen, and centring it made the detailed feed jump on every open.
        inputContainerEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }

      // Replying to a reply no longer seeds an @mention — the "Replying to" banner and the
      // reference row on the sent reply say who it's for, and `handleNotifications` notifies
      // them directly. So the editor only ever gets the saved draft, if there is one.
      this._win.setTimeout(() => {
        const input = this.replyInput;
        if (!input) return;
        const draft = this.storageService.getItem(StorageKeys.POST_REPLY_DRAFT_MAP)?.[this.comment.id]?.body;
        if (draft) {
          // setMessageValue writes the HTML after its own 50ms delay and focuses at the
          // start; put the caret at the end once that has landed. (Quill — beta — keeps
          // its own selection, so it only needs focus.)
          input.setMessageValue(draft);
          if (input.useQuill) input.focusMessageInput();
          else input.cursorToEnd(100);
        } else {
          input.focusMessageInput();
        }
      }, 200);
    }
  }

  watchVideo(id) {
    const video = this.contentService.videosById[id];
    if (!video) {
      // Fallback when the video isn't in the local catalog cache yet.
      window.open(`${environment.fenderUrl}learn/browse?viewingVideo=${id}`, "_blank");
      return;
    }
    this._eventbusService.emit(this._eventbusService.types.playVideo, {
      id: video.platform_id,
      title: video.title,
      type: video.platform,
      thumb: video.image,
      presonus_id: video.id,
    });
  }

  showImage(src) {
    this._eventbusService.emit(this._eventbusService.types.viewFullscreenImage, src);
  }

  clearReplyToReply(comment) {
    this.replyingToReply = undefined;
    // `#replyInput` only exists inside `@if (comment.replying)` — it can already be gone by
    // the time an async sendReply().then() callback collapses the reply box, so guard the
    // access the same way the template does (`@if (replyInput && ...)`).
    this.replyInput?.setMessageValue("");
    comment.replying = false;
    this.changeDetector.detectChanges();
  }

  /**
   * The member's signature for the community this thread lives in (DENG-847), or "".
   * Replies are signed like top-level posts; beta threads never are — signatures are per
   * community and a beta program isn't one.
   */
  get replySignature(): string {
    if (!this.isCommunityPost || this.betaStringId) return "";
    return this.communityService.getCommunitySignature(this.comment?.community_string_id || this.communityStringId);
  }

  clearReplyUI(comment) {
    // See clearReplyToReply: `replyInput` may be undefined once the reply box is torn down.
    this.replyInput?.setMessageValue("");
    this.replyingToComment = undefined;
    this.replyingToReply = undefined;
    this.replySignatureEnabled = true;
    comment.replying = false;
    this.changeDetector.detectChanges();
  }

  droppedFile(args) {
    if (this.isCommunityPost) {
      this.attachFile(args);
      this.uploader.clearQueue();
    }
  }

  sendReply(args, comment) {
    if (!this.user) {
      this.appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => this.sendReply(args, comment),
        () => {}
      );
      return;
    }
    if (
      (this.isCommunityPost && this.pinReplyingToPost && !this.comment.pins) ||
      (this.isCommunityPost && this.comment.pins && !this.comment.pins.includes(this.userService.user?.id)) ||
      (this.isCommunityPost && !this.pinReplyingToPost && this.comment.pins && this.comment.pins.includes(this.userService.user?.id))
    ) {
      this.pinPost();
    }
    let msg = args.value;
    if (this.isCommunityPost) {
      if ((msg && msg.trim()) || (this.uploadedAttachments && this.uploadedAttachments.length)) {
        let replyArgs: any = {
          author: {
            name: this.userService.user?.firstName + " " + this.userService.user?.lastName,
            photo: this.userService.user?.photoURL,
            id: this.userService.user?.id,
          },
          parentID: comment.key,
          created: new Date(),
        };
        // The reply being answered, when sent from a reply's own reply button. Id only —
        // see `_hydrateReplyTarget`.
        const replyingToID = this.replyingToReply?.id || this.replyingToReply?.key;
        if (replyingToID) replyArgs.replyingToID = replyingToID;
        if (comment.channels) {
          replyArgs.channels = comment.channels;
        }
        if (comment.topics) {
          replyArgs.topics = comment.topics;
        }
        if (this.betaStringId) {
          replyArgs.beta_string_id = this.betaStringId;
        } else {
          // Reply inherits the parent's community. Fall back to the component's
          // `communityStringId` if the parent is missing the field (older post created
          // before task #34 shipped). Keeps replies filterable by community in Algolia
          // + Firestore alongside their parents.
          const inheritedCommunity = comment.community_string_id || this.communityStringId;
          if (inheritedCommunity) replyArgs.community_string_id = inheritedCommunity;
        }
        if (this.replyInput.selectedArticlesInMessage && this.replyInput.selectedArticlesInMessage.length) {
          let articleEntites = [];
          this.replyInput.selectedArticlesInMessage.forEach((item) => {
            articleEntites.push({
              article: item.article,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
            msg = msg.replace(item.editorSelector, item.selector + " ");
          });
          replyArgs.articles = articleEntites;
        }

        if (this.replyInput.selectedLearnContentInMessage && this.replyInput.selectedLearnContentInMessage.length) {
          let learnContentEntities = [];
          this.replyInput.selectedLearnContentInMessage.forEach((item) => {
            learnContentEntities.push({
              content: item.content,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
            msg = msg.replace(item.editorSelector, item.selector + " ");
          });
          replyArgs.learn_content = learnContentEntities;
        }

        if (this.replyInput.publicFileLinksInMessage && this.replyInput.publicFileLinksInMessage.length) {
          let publicFileLinkEntities = [];
          this.replyInput.publicFileLinksInMessage.forEach((item) => {
            publicFileLinkEntities.push({
              publicFile: item.publicFile,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
            msg = msg.replace(item.editorSelector, item.selector + " ");
          });
          replyArgs.public_file_links = publicFileLinkEntities;
        }

        if (this.replyInput.selectedCategoriesSectionsInMessage && this.replyInput.selectedCategoriesSectionsInMessage.length) {
          let sectionCategoryEntities = [];
          this.replyInput.selectedCategoriesSectionsInMessage.forEach((item) => {
            sectionCategoryEntities.push({
              category_or_section: item.category_or_section,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
            msg = msg.replace(item.editorSelector, item.selector + " ");
          });
          replyArgs.categories_or_sections = sectionCategoryEntities;
        }

        const { messageEmbeds } = this.replyInput;
        if (messageEmbeds.length > 0) {
          replyArgs.embeds = messageEmbeds.map(({ id, type, subtype }) => {
            const embed = { id, type, subtype, src: "" };
            if (type == "youtube") {
              embed.src = `https://www.youtube.com/embed/${id}`;
            } else if (type == "soundcloud") {
              embed.src = `https://w.soundcloud.com/player/?url=${id}`;
            } else {
              embed.src = `https://open.spotify.com/embed/${subtype}/${id}`;
            }
            return embed;
          });
        }

        // Mentions resolve last: finalizeMessage measures its character offsets
        // against the string it returns, so every other selector rewrite above
        // has to have happened already. It also does the HTML-to-text pass this
        // path used to run inline, and now produces the same plain-text shape
        // every other surface does.
        const finalizedReply = this.replyInput.finalizeMessage(msg);
        msg = finalizedReply.message;
        if (finalizedReply.mentions.length) replyArgs.mentions = finalizedReply.mentions;
        this.replyInput.selectedMentionsInMessage = [];

        // Signature goes on after finalizeMessage, same as `CreatePostInputComponent._withSignature`:
        // appending cannot shift the mention offsets, which are all before it.
        const signature = this.replySignature;
        if (signature && this.replySignatureEnabled) {
          msg = msg ? `${msg}\n\n${signature}` : signature;
          // Also on its own, so readers can set it apart from the body (`splitPostSignature`).
          replyArgs.signature = signature;
        }

        if (msg) replyArgs.message = msg;

        if (this.uploadingFiles && this.uploadingFiles.length) {
          this.appService.alert(this.translate.instant("com.wait_file_upload"));
          return;
        } else if (this.uploadedAttachments && this.uploadedAttachments.length) {
          replyArgs.files = this.uploadedAttachments;
        }

        this.removeAttachment();
        this.updateReplyDrafts(null);
        this.communityService.addPost(replyArgs, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection).then(
          (result) => {
            const reply = {
              ...replyArgs,
              id: result.id,
              key: result.id,
              type: "reply",
            };
            this.communityService.addSoftReply(reply, comment);
            this._hydrateReplyTarget(reply);
            this.communityService.sortActiveParentPosts();

            const cache = this.communityService.communityListBackCache;
            if (cache?.activeParentThreadMap) {
              const parentId = comment.id || comment.key;
              if (!cache.activeParentThreadMap[parentId]) cache.activeParentThreadMap[parentId] = [];
              const alreadyInCache = cache.activeParentThreadMap[parentId].some((r) => r.id === reply.id || r.key === reply.id);
              if (!alreadyInCache) cache.activeParentThreadMap[parentId].push(reply);

              if (cache.activeParentPosts?.length && this.communityService.betaSortBy === "recentactivity") {
                const getTs = (dateObj: any): number => {
                  if (dateObj == null) return 0;
                  try {
                    if (typeof dateObj.toDate === "function") {
                      const t = dateObj.toDate().getTime();
                      return Number.isFinite(t) ? t : 0;
                    }
                    const t = new Date(dateObj).getTime();
                    return Number.isFinite(t) ? t : 0;
                  } catch {
                    return 0;
                  }
                };
                const getActivityTimestamp = (post: any): number => {
                  const replies = cache.activeParentThreadMap[post.key] || cache.activeParentThreadMap[post.id] || [];
                  if (replies.length > 0) {
                    const times = replies.map((r) => getTs(r.created)).filter((t) => t > 0);
                    if (times.length) return Math.max(...times);
                  }
                  return getTs(post.created);
                };
                cache.activeParentPosts.sort((a, b) => {
                  const diff = getActivityTimestamp(b) - getActivityTimestamp(a);
                  return diff !== 0 ? diff : String(a.key).localeCompare(String(b.key));
                });
              }
            }

            const page = this.avoService.getPageProperties({ pageName: "Community" });
            const channels = this.getChannelName(this.comment.channels);
            const channelName = Array.isArray(channels) ? channels.join(",") : channels;
            const extra = {
              name: "Community",
              channelName: channelName,
              eventType: "post",
              postId: parseInt(result.id, 10) || undefined,
              attachmentExtension: this.uploadedAttachments?.[0]?.extension ?? undefined,
              interactionType: "created",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            this.avoService.trackEvent().communityViewed(payload);
            // With its id, so the notification can open the thread scrolled to this reply.
            this.handleNotifications({ ...replyArgs, id: result.id });
            this.clearReplyUI(comment);
          },
          (err) => {
            // The reply box is only cleared on success, so the text is still there to send again.
            console.warn("[comment] reply addPost failed", err);
            this.appService.alert("Your reply didn't send. Check your connection and try again — your text is still here.");
          }
        );
      }
    } else {
      this.replied.next({
        comment: comment,
        reply: msg,
        replyingTo: this.replyingToReply ? this.replyingToReply : undefined,
      });
      this.clearReplyUI(comment);
    }
  }

  handleNotifications(replyArgs) {
    let notifiedMap = {};
    let betaProgramName: string = "";
    if (this.betaStringId) {
      const betaDetail = this.betaService.betaDetailByStringId[this.betaStringId];
      betaProgramName = betaDetail?.title && betaDetail.title.trim() ? betaDetail.title : this.betaStringId;
    }
    let defaultTitle = this.betaStringId ? `${this.user?.firstName} replied to your post in the ${betaProgramName} beta community.` : this.user?.firstName + " replied to your post.";
    let defaultReplyData = {
      author: replyArgs.author,
      message: this.helperService.htmlToText(replyArgs.message),
    };
    let notification = this.notificationService.createCommunityPostNotification(
      "community_post_reply",
      this.userService.user,
      { id: this.comment.id, community_string_id: this.comment.community_string_id, beta_string_id: this.betaStringId },
      replyArgs,
      { id: this.comment.id, message: this.comment.message, author: this.comment.author, community_string_id: this.comment.community_string_id, beta_string_id: this.betaStringId },
      undefined,
      this.communityStringId,
      this.betaStringId
    );
    notification.title = defaultTitle;
    if (this.comment.author.id != this.user?.id) {
      // dont send an email if youre replying to your own comment.
      if (!this.replyingToReply || this.replyingToReply.author.id != this.comment.author.id) {
        // dont send notification if this post is a reply to a reply the original author made, because they are getting a notification below.
        this.notificationService.sendNotification({ notification: notification, user_ids: [this.comment.author.id], clients: ["rollup", "mobile", "email"] }).subscribe();
        notifiedMap[this.comment.author.id] = true;
      }
    }
    if (this.replyingToReply && this.replyingToReply.author.id != this.user?.id) {
      this.notificationService.sendNotification({ notification: notification, user_ids: [this.replyingToReply.author.id], clients: ["rollup", "mobile", "email"] }).subscribe();
      notifiedMap[this.replyingToReply.author.id] = true;
    }

    if (replyArgs.mentions && replyArgs.mentions.length) {
      // add a notification for users who were mentioned, but not if the reply was directed to them or they were notified already.
      let mentionsToNotify = [];
      replyArgs.mentions.forEach((item) => {
        if (item.id != this.userService.user?.id && item.id != this.comment.author.id && item.id != this.replyingToReply?.author.id && !notifiedMap[item.id]) {
          mentionsToNotify.push(item.id);
          notifiedMap[item.id] = true;
        }
      });
      if (mentionsToNotify.length) {
        let mentionNotification = this.notificationService.createCommunityPostNotification(
          "community_post_mention",
          this.userService.user,
          { id: this.comment.id, message: replyArgs.message, author: replyArgs.author, community_string_id: this.comment.community_string_id, beta_string_id: this.betaStringId },
          undefined,
          {
            id: this.comment.id,
            message: this.comment.message,
            author: this.comment.author,
            community_string_id: this.comment.community_string_id,
            beta_string_id: this.betaStringId,
          },
          undefined,
          this.communityStringId,
          this.betaStringId
        );
        this.notificationService.sendNotification({ notification: mentionNotification, user_ids: mentionsToNotify, clients: ["rollup", "mobile", "email"] }).subscribe();
      }
    }
    if (this.comment.pins && this.comment.pins.length) {
      let pinsToNotify = [];
      this.comment.pins.forEach((id) => {
        // only notify them if they haven't been notified for some other reason, and the author isnt you.
        if (!notifiedMap[id] && id != replyArgs.author.id) {
          pinsToNotify.push(id);
        }
      });
      if (pinsToNotify.length) {
        let pinnedNotification = this.notificationService.createCommunityPostNotification(
          "community_pinned_post_reply",
          this.userService.user,
          { id: this.comment.id, community_string_id: this.comment.community_string_id, beta_string_id: this.betaStringId },
          replyArgs,
          { id: this.comment.id, message: this.comment.message, author: this.comment.author, community_string_id: this.comment.community_string_id, beta_string_id: this.betaStringId },
          undefined,
          this.communityStringId,
          this.betaStringId
        );
        this.notificationService.sendNotification({ notification: pinnedNotification, user_ids: pinsToNotify, clients: ["rollup", "mobile", "email"] }).subscribe();
      }
    }

    if (this.betaStringId && this.betaManagerNotificationPreferences) {
      let notificationIds = [];
      // send out notifications to beta managers if applicable
      for (var i in this.betaManagerNotificationPreferences) {
        if (this.betaManagerNotificationPreferences[i].all_replies && !notifiedMap[i] && i != replyArgs.author.id) {
          notificationIds.push(i);
        }
      }
      if (notificationIds.length) {
        let betaNotification = this.notificationService.createNewBetaPost(
          this.userService.user,
          { id: this.comment.id, message: replyArgs.message, author: replyArgs.author, subject: "" },
          this.betaStringId,
          betaProgramName
        );
        betaNotification.title = `${this.user?.firstName} replied to a post in the ${betaProgramName} beta program.`;
        betaNotification.payload.reply = defaultReplyData;
        this.notificationService.sendNotification({ notification: betaNotification, user_ids: notificationIds, clients: ["rollup", "mobile", "email"] }).subscribe();
      }
    }
  }

  likesContainerEnter(data) {
    this.likesContainerHovering = true;
    this.likeUserNames(data.likes);
  }

  likesContainerLeave(data) {
    this.likesContainerHovering = false;
    this.shouldShowLikesList = false;
  }

  likeMouseEnter(data) {
    this.shouldShowLikesList = data.id;
    this.likeUserNames(data.likes);
  }

  likeMouseLeave() {
    this._win.setTimeout(() => {
      if (!this.likesContainerHovering) this.shouldShowLikesList = false;
    }, 200);
  }

  public activeHoveringLikeUserName: any;
  public activeHoveringLikeUserNameList = {};
  likeBubbleMouseEnter(userId) {
    if (this.communityService.cachedNames[userId]) {
      this.activeHoveringLikeUserName = this.communityService.cachedNames[userId];
      this.activeHoveringLikeUserNameList[userId] = this.communityService.cachedNames[userId];
    } else {
      this.communityService.getUserProfile(userId).then((result: any) => {
        this.activeHoveringLikeUserName = result.firstName + " " + result.lastName;
        this.activeHoveringLikeUserNameList[userId] = result.firstName + " " + result.lastName;
      });
    }
  }

  likeUserNames(userIds) {
    userIds.forEach((userId) => {
      if (this.communityService.cachedNames[userId]) {
        this.activeHoveringLikeUserNameList[userId] = this.communityService.cachedNames[userId];
      } else {
        this.communityService.getUserProfile(userId).then((result: any) => {
          this.activeHoveringLikeUserNameList[userId] = result.firstName + " " + result.lastName;
        });
      }
    });
  }

  likeBubbleMouseLeave() {
    this.activeHoveringLikeUserName = undefined;
  }

  likeBubbleMouseLeaveByFocus(idx, total) {
    this.activeHoveringLikeUserName = undefined;
    if (idx === total - 1) this.shouldShowLikesList = false;
  }

  /** `anchor`: the ••• that was pressed — the menu opens there rather than as the bottom sheet. */
  msgOptions(msg, anchor?: EventTarget) {
    let args: any = {
      // Anchored to the post's own •••, an excerpt of it would only repeat what's beside it.
      title: anchor ? "" : this.helperService.stripHtml(msg.message).substring(0, 200) + "...",
      actions: [],
      anchor,
    };
    // Save and Share used to be pills in the post's row; the row is now [•••] [reply] ——
    // [reactions], so they lead this menu. Top-level community posts only, never a beta post.
    const sharable = !this.reviewing && this.isCommunityPost && !msg.parentID && !this.betaStringId;
    const isSaved = !!msg.pins?.includes(this.userService.user?.id);
    const saveLabel = this.translate.instant(isSaved ? "com.unsave_post" : "com.save_post");
    const shareLabel = this.translate.instant("com.share_post");
    if (sharable) {
      args.actions.push({ label: saveLabel, icon: isSaved ? "bookmark" : ["far", "bookmark"] });
      args.actions.push({ label: shareLabel, icon: "arrow-up-from-bracket" });
    }
    if (msg.author.id == this.user?.id) {
      args.actions.push({ label: "Edit " + (this.isCommunityPost ? "Post" : "Comment"), icon: "pencil-alt" });
      args.actions.push({ label: "Delete " + (this.isCommunityPost ? "Post" : "Comment"), icon: "trash-alt" });
    } else {
      if (this.user?.active_subscription && !this.communityService.connectionMap[msg.author.id]) args.actions.push({ label: "Connect with " + msg.author.name, icon: "user-plus" });

      if (this.user?.is_community_wizard) {
        args.actions.push({ label: "Delete " + (this.isCommunityPost ? "Post (Admin)" : "Comment (Admin)"), icon: "trash-alt" });
        args.actions.push({ label: "Edit " + (this.isCommunityPost ? "Post (Admin)" : "Comment (Admin)"), icon: "pencil-alt" });
      }
    }

    if (this.user?.is_sphere_admin && this.isCommunityPost && !this.betaStringId) {
      args.actions.push(
        msg.sphere_only ? { label: `Unmark as ${this.env.studioOnePlusBrandName} Only (Admin)`, icon: "lock-open" } : { label: `Mark as ${this.env.studioOnePlusBrandName} Only (Admin)`, icon: "lock" }
      );
      args.actions.push({ label: "Moderate Posts by " + msg.author.name, icon: "user-shield" });
    }
    if (!this.betaStringId) {
      if (msg.flags && msg.flags.includes(this.userService.user?.id)) {
        args.actions.push({ label: "Unflag " + (this.isCommunityPost ? "Post" : "Comment"), icon: ["far", "flag"] });
      } else {
        args.actions.push({ label: "Flag " + (this.isCommunityPost ? "Post" : "Comment"), icon: "flag" });
      }
    }

    if (this.user?.is_sphere_admin && msg.flags && msg.flags.length && !msg.cleared) args.actions.push({ label: "Clear Flag (Admin)", icon: "check-circle" });
    // DENG-1294: community moderators put a post into, or take it out of, the Featured rail.
    const featureCommunity = msg.community_string_id || this.communityStringId;
    const canFeature =
      !!this.env.features?.communityFeaturedRail &&
      this.isCommunityPost &&
      !msg.parentID &&
      !this.betaStringId &&
      !!this.user &&
      this.communitiesService.isCommunityModerator(this.user, featureCommunity);
    if (canFeature) args.actions.push(msg.featured ? { label: "Remove from Featured", icon: "thumbtack-slash" } : { label: "Pin to Featured Rail", icon: "thumbtack" });
    // Share's chooser leads with Copy Link, so the separate item stays only where Share isn't offered.
    if (this.isCommunityPost && !msg.parentID && !sharable) args.actions.push({ label: "Copy Direct Link to Post", icon: "link" });
    if (this.isCommunityPost && msg.parentID) args.actions.push({ label: "Copy Direct Link to Reply", icon: "link" });
    this._eventbusService.emit(this._eventbusService.types.showActionChooser, args);
    this._eventbusService
      .observe(this._eventbusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          if (sharable && result == saveLabel) {
            this.pinPost();
          } else if (sharable && result == shareLabel) {
            this.sharePost();
          } else if (result == "Edit Comment" || result == "Edit Post" || result == "Edit Post (Admin)" || result == "Edit Comment (Admin)") {
            if (this.isCommunityPost) {
              this.editPost(msg);
            } else {
              this.editMessageClicked.next(msg);
            }
          } else if (result == "Delete Message" || result == "Delete Post" || result == "Delete Comment" || result == "Delete Post (Admin)" || result == "Delete Comment (Admin)") {
            if (this.isCommunityPost) {
              this.deletePost(msg, result == "Delete Post (Admin)");
            } else {
              if (this.user?.is_admin && result == "Delete Post (Admin)") {
                this.adminDeleteMessageClicked.next(msg);
              } else {
                this.deleteMessageClicked.next(msg);
              }
            }
          } else if (result == "Flag Comment" || result == "Flag Post" || result == "Unflag Comment" || result == "Unflag Post") {
            this.flag(msg);
          } else if (result == "Clear Flag (Admin)") {
            this.clearFlag(msg);
          } else if (result == "Pin to Featured Rail" || result == "Remove from Featured") {
            this.communityService.setPostFeatured(msg, result == "Pin to Featured Rail").catch((error) => {
              this.appService.alert(error?.message || "That post's Featured setting couldn't be changed.");
            });
          } else if (result == "Copy Direct Link to Reply") {
            this.copyLink(msg, true);
          } else if (result == "Copy Direct Link to Post") {
            this.copyLink(msg);
          } else if (result == `Unmark as ${this.env.studioOnePlusBrandName} Only (Admin)` || result == `Mark as ${this.env.studioOnePlusBrandName} Only (Admin)`) {
            this.markUnmarkSphereOnly(msg);
          } else if (result.indexOf("Connect with") > -1) {
            this.communityService.addConnection(msg.author.id).subscribe((result) => {
              this.appService.alert("Your connection request has been sent.");
            });
          } else if (result.indexOf("Moderate") > -1) {
            this.router.navigate(["/admin/user_posts"], { queryParams: { user_id: msg.author.id } });
          }
        }
      });
  }

  replyContentChanged(args) {
    this.updateReplyDrafts(args);
  }

  updateReplyDrafts(replyText) {
    let existingReplyDrafts = this.storageService.getItem(StorageKeys.POST_REPLY_DRAFT_MAP);
    if (!existingReplyDrafts) existingReplyDrafts = {};
    let rawTextReply = this.helperService.stripHtml(replyText);
    if (rawTextReply) {
      let draftArgs = {
        body: replyText,
      };
      existingReplyDrafts[this.comment.id] = draftArgs;
      this.storageService.setItem(StorageKeys.POST_REPLY_DRAFT_MAP, existingReplyDrafts);
    } else {
      delete existingReplyDrafts[this.comment.id];
      this.storageService.setItem(StorageKeys.POST_REPLY_DRAFT_MAP, existingReplyDrafts);
    }
  }

  /** Share (in •••) just copied the link: the row says "Link copied" for a moment. */
  public linkCopied = false;
  private _linkCopiedTimer: any;

  async sharePost() {
    if (this.betaStringId) return;
    const outcome = await this._shareService.share(this.comment, { communityStringId: this.communityStringId });
    if (outcome !== "copied") return;
    this.linkCopied = true;
    clearTimeout(this._linkCopiedTimer);
    this._linkCopiedTimer = setTimeout(() => {
      this.linkCopied = false;
      this.changeDetector.markForCheck();
    }, 2000);
    this.changeDetector.markForCheck();
  }

  copyLink(msg, isReply?: boolean) {
    const url =
      this.env.fenderUrl +
      buildCommunityPostPath({
        postId: isReply ? msg.parentID : msg.id,
        communityStringId: msg.community_string_id || this.communityStringId,
        betaStringId: this.betaStringId,
        replyId: isReply ? msg.id : undefined,
      });
    this._clipboardService.copy(url);
  }

  showVideo(video) {
    this._eventbusService.emit(this._eventbusService.types.playVideo, {
      id: video.video_id,
      title: video.title,
      type: video.type,
      thumb: video.thumb,
    });
  }

  markUnmarkSphereOnly(comment) {
    this.communityService.markUnmarkSphereOnly(comment).then(() => {
      comment.sphere_only = comment.sphere_only ? false : true;
    });
  }

  flag(comment) {
    if (this.user) {
      if (comment.flags && comment.flags.includes(this.userService.user?.id)) {
        this.unflag(comment);
      } else {
        let args: any = {
          title: this.translate.instant("com.choose_flag_reason"),
          actions: [
            this.translate.instant("com.flag_reason.inappropriate"),
            this.translate.instant("com.flag_reason.privacy"),
            this.translate.instant("com.flag_reason.harassment"),
            this.translate.instant("com.flag_reason.hateful"),
            this.translate.instant("com.flag_reason.copyright"),
            this.translate.instant("com.flag_reason.other"),
          ],
        };

        this._win.setTimeout(() => {
          this._eventbusService.emit(this._eventbusService.types.showActionChooser, args);
          this._eventbusService
            .observe(this._eventbusService.types.actionChosen)
            .pipe(take(1))
            .subscribe((reason) => {
              if (reason) {
                if (this.isCommunityPost) {
                  this.communityService.flagUnflagPost(comment, reason).then((result) => {
                    this.appService.alert(this.translate.instant("com.moderator_notified"));
                  });
                } else {
                  this.flagged.next(comment);
                }
                let args: any = {
                  userName: this.userService.user?.firstName + " " + this.userService.user?.lastName,
                  commentUserName: comment.author.name,
                  commentPhoto: comment.author.photo,
                  comment: comment.message,
                  reason: reason,
                  commentKey: comment.key,
                };
                if (this.video) {
                  args.videoTitle = this.video.title;
                  args.videoKey = this.video.key;
                }
                this.contentService.sendEmail(args, "notify_flagged_comment").subscribe(() => {});
                if (this.isCommunityPost && reason == this.translate.instant("com.flag_reason.copyright")) {
                  this._clipboardService.copy(this.env.fenderUrl + buildCommunityPostPath({ postId: comment.id, communityStringId: comment.community_string_id || this.communityStringId }));
                  this.appService.alert(this.translate.instant("com.copyright_violation_alert"));
                  window.location.href = "https://www.presonus.com/Claims-of-Copyright-Infringement-and-Related-Issues";
                }
                const page = this.avoService.getPageProperties({ pageName: "Community" });
                const channels = this.getChannelName(this.comment.channels);
                const channelName = Array.isArray(channels) ? channels.join(",") : channels;
                const extra = {
                  name: "Community",
                  channelName: channelName,
                  eventType: "post",
                  postId: parseInt(comment.id, 10) || undefined,
                  interactionType: "flagged",
                };
                const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
                this.avoService.trackEvent().communityViewed(payload);
              }
            });
        }, 1000);
      }
    } else {
      this.appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => {
          this.flag(comment);
        },
        (error) => {}
      );
    }
  }

  getChannelName(channels) {
    return channels === undefined ? [] : channels;
  }

  unflag(comment) {
    if (this.isCommunityPost) {
      this.communityService.flagUnflagPost(comment);
    } else {
      this.unflagged.next(comment);
    }
  }

  upgrade() {
    this.router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  private hydrateCommentReactionState(comment: any) {
    if (!comment) return;
    const reactions = normalizeReactions(comment);
    comment.reactions = reactions;
    comment.iLike = getMyReactions(reactions, this.user?.id).includes("like");
    comment.isEmojiOnly = isEmojiOnlyMessage(comment.htmlMessage || comment.message);
  }

  onReactionChange(event: ReactionChangeEvent) {
    const { comment, selectedType, previousTypes, nextTypes } = event;
    if (this.isCommunityPost) {
      if (!this.user) {
        this.appService.promptSimpleSignup(SimpleSignupComponent).then(
          () => this.like(comment),
          () => {}
        );
        return;
      }
      if (this.canReact) {
        this.persistCommunityReaction(comment, selectedType, previousTypes, nextTypes);
      } else {
        // Revert optimistic UI when the participation gate blocks the write
        const userId = this.userService.user?.id;
        if (userId) {
          const reverted = { ...(comment.reactions || {}) };
          if (previousTypes.length) reverted[userId] = previousTypes;
          else delete reverted[userId];
          comment.reactions = reverted;
        }
        this.hydrateCommentReactionState(comment);
        this.explainReactionBlock();
      }
    } else {
      if (this.user) {
        comment.nextReactions = nextTypes;
        comment.selectedReactionType = selectedType;
        this.liked.next(comment);
      } else {
        this.appService.promptSimpleSignup(SimpleSignupComponent).then(
          () => {
            this.onReactionChange(event);
          },
          () => {}
        );
      }
    }
  }

  like(comment) {
    const previousTypes = getMyReactions(normalizeReactions(comment), this.user?.id);
    this.onReactionChange({
      comment,
      selectedType: "like",
      previousTypes,
      nextTypes: toggleReaction(previousTypes, "like"),
    });
  }

  /**
   * Thin wrapper over `CommunityReactionService.persist`. This component keeps ownership of
   * the two things that are genuinely its own — resolving the parent post for a reply (so a
   * beta reply's notification links to the thread, not the reply doc) and formatting the
   * channel names for Avo. Everything after that is shared with the list view.
   */
  private persistCommunityReaction(comment, selectedType: ReactionType, previousTypes: ReactionType[], nextTypes: ReactionType[]) {
    const channels = this.getChannelName(this.comment.channels);
    this.communityReactionService.persist({
      post: comment || this.comment,
      selectedType,
      previousTypes,
      nextTypes,
      communityStringId: this.communityStringId,
      betaStringId: this.betaStringId,
      originalPost:
        comment.parentID || this.betaStringId
          ? {
              id: this.comment.id,
              message: this.comment.message,
              author: this.comment.author,
              community_string_id: this.comment.community_string_id,
              beta_string_id: this.betaStringId,
            }
          : undefined,
      channelName: Array.isArray(channels) ? channels.join(",") : channels,
    });
  }

  selectBetaPostStatus(event) {
    this.comment.beta_status = event;
    this.communityService.updatePost(this.comment.key, { beta_status: event }, `beta_community_posts`);
  }

  selectBetaPostType(event) {
    this.comment.beta_type = event;
    this.communityService.updatePost(this.comment.key, { beta_type: event }, `beta_community_posts`);
  }

  toggleMarkingAsReviewed() {
    this.markingAsReviewed = !this.markingAsReviewed;
    this.changeDetector.detectChanges();
  }

  private syncSavedBetaReviewJiraBrowseUrl(): void {
    const details = this._comment?.beta_review_details;
    if (!details) {
      this.savedBetaReviewJiraBrowseUrl = null;
      return;
    }
    this.savedBetaReviewJiraBrowseUrl = details.jira_ticket_link || jiraBrowseUrlFromTicketKey(details.jira_ticket_number);
  }

  markAsReviewed() {
    const normalizedKey = normalizeJiraIssueKey(this.betaReviewDetails.jira_ticket_number ?? "");
    if (normalizedKey && !isValidJiraIssueKey(this.betaReviewDetails.jira_ticket_number ?? "")) {
      this.appService.alert("Please enter a valid Jira ticket key (for example PRS-1234) before marking this post as reviewed.");
      return;
    }
    this.betaReviewDetails.jira_ticket_number = normalizedKey;
    this.betaReviewDetails.jira_ticket_link = jiraBrowseUrlFromTicketKey(normalizedKey) ?? "";
    this.betaReviewDetails.reviewed_by = {
      id: this.userService.user?.id,
      name: this.userService.user?.firstName + " " + this.userService.user?.lastName,
      photo: this.userService.user?.photoURL,
    };
    this.betaReviewDetails.reviewed_on = new Date();
    this.communityService.updatePost(
      this.comment.key,
      {
        beta_review_details: this.betaReviewDetails,
        reviewed: true,
      },
      `beta_community_posts`
    );
    this.comment.beta_review_details = { ...this.betaReviewDetails };
    this.comment.reviewed = true;
    this.reviewedOnDate = this.betaReviewDetails.reviewed_on;
    this.syncSavedBetaReviewJiraBrowseUrl();
    this.markingAsReviewed = false;
    this.changeDetector.detectChanges();
  }

  removeReviewed() {
    this.appService.confirm(this.translate.instant("com.confirm_remove_reviewed")).then((result) => {
      if (!result) return;
      this.communityService.updatePost(
        this.comment.key,
        {
          reviewed: false,
          beta_review_details: null,
        },
        `beta_community_posts`
      );
      this.comment.beta_review_details = null;
      this.comment.reviewed = false;
      this.reviewedOnDate = undefined;
      this.betaReviewDetails = {
        reviewed_by: {},
        reviewed_at: new Date(),
        review_text: "",
        jira_ticket_number: "",
        jira_ticket_link: "",
      };
      this.savedBetaReviewJiraBrowseUrl = null;
      this.markingAsReviewed = false;
      this.changeDetector.detectChanges();
    });
  }

  createJiraTicket() {
    const defaultProject = (this.betaStringId && BETA_TO_JIRA_PROJECT[this.betaStringId]) || "SO";
    const modalRef = this.appService.showModal(CreateJiraTicketComponent, { size: "lg" });
    modalRef.componentInstance.data = {
      summary: this.comment.subject,
      description: this.helperService.htmlToMarkdown(this.comment.message),
      project: defaultProject,
    };
    if (this.comment.files) modalRef.componentInstance.attachments = this.comment.files;
    // Jira ticket bodies are read by Fender engineers — always use fenderUrl so the link
    // resolves to my.fender.com, not my.presonus.com (which is what `env.mypresonusUrl`
    // still points to on Fender prod builds since fender-overrides doesn't touch it).
    modalRef.componentInstance.linkToPost = this.env.fenderUrl + buildCommunityPostPath({ postId: this.comment.id, betaStringId: this.betaStringId });
    modalRef.componentInstance.completed.pipe(take(1)).subscribe((result) => {
      this.betaReviewDetails.jira_ticket_number = result.key;
      this.betaReviewDetails.jira_ticket_link = jiraBrowseUrlFromTicketKey(result.key) ?? "";
      this.markAsReviewed();
    });
  }

  clearFlag(comment) {
    if (this.isCommunityPost) {
      this.communityService.clearFlag(comment.key);
    } else {
      this.flagCleared.next(comment);
    }
  }

  deletePost(post, admin?) {
    this.appService.confirm(this.translate.instant("com.confirm_delete_post")).then((result) => {
      this.communityService.deletePost(post ? post.key : this.comment.key, admin, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection).then((result) => {});
    });
  }

  selectChannel(channel) {
    if (this.betaStringId) {
      this.channelSelected.next(channel);
    } else if (this.communityService.skillsMap[channel]) {
      this.channelSelected.next(channel);
    } else if (this.communityService.extraChannelsMap[channel] && this.communityService.extraChannelsMap[channel].active) {
      this.channelSelected.next(channel);
    }
  }

  likePost(comment?, isLiking?) {
    if (isLiking && this.userService.user?.id != comment.author.id) {
      let notification = this.notificationService.createCommunityPostNotification(
        "community_post_liked",
        this.userService.user,
        comment,
        undefined,
        comment.parentID || this.betaStringId
          ? {
              id: this.comment.id,
              message: this.comment.message,
              author: this.comment.author,
              community_string_id: this.comment.community_string_id,
              beta_string_id: this.betaStringId,
            }
          : undefined,
        undefined,
        this.communityStringId,
        this.betaStringId
      );
      this.notificationService
        .sendNotification({
          notification: notification,
          user_ids: [comment.author.id],
          clients: ["rollup", "mobile", "email"],
        })
        .subscribe();
    }
    this.communityService.likeUnlikePost(comment || this.comment, isLiking, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection);
    if (isLiking) {
      const page = this.avoService.getPageProperties({ pageName: "Community" });
      const channels = this.getChannelName(this.comment.channels);
      const channelName = Array.isArray(channels) ? channels.join(",") : channels;
      const extra = {
        name: "Community",
        channelName: channelName,
        eventType: "post",
        postId: parseInt(comment.id, 10) || undefined,
        interactionType: "liked",
      };
      const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
      this.avoService.trackEvent().communityViewed(payload);
    }
  }

  removeChannel(channel) {
    if (this.betaStringId && this.isBetaManager) {
      this.comment.channels = this.comment.channels.filter((item) => item != channel);
      this.communityService.updatePost(this.comment.key, { channels: this.comment.channels }, "beta_community_posts");
    }
  }

  addChannel(channel) {
    if (this.betaStringId && this.isBetaManager) {
      if (!this.comment.channels) this.comment.channels = [];
      if (this.comment.channels.includes(channel.string_id)) return;
      this.comment.channels.push(channel.string_id);
      this.communityService.updatePost(this.comment.key, { channels: this.comment.channels }, "beta_community_posts");
      this.shouldShowChannelSelector = false;
    }
  }

  pinPost() {
    if (!this.userService.user) {
      this.appService.promptSimpleSignup(SimpleSignupComponent);
      return;
    }
    this.communityService.pinUnpinPost(this.comment, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection);
    const page = this.avoService.getPageProperties({ pageName: "Community" });
    const channels = this.getChannelName(this.comment.channels);
    const channelName = Array.isArray(channels) ? channels.join(",") : channels;
    const extra = {
      name: "Community",
      channelName: channelName,
      eventType: "post",
      postId: parseInt(this.comment.id, 10) || undefined,
      interactionType: "pinned",
    };
    const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
    this.avoService.trackEvent().communityViewed(payload);
  }

  editPost(comment?, isPinnedBetaPost?) {
    let editingComment = { ...(comment || this.comment) };
    const modalRef = this.appService.showModal(EditCommentComponent, { size: "lg" });
    modalRef.componentInstance.comment = { ...editingComment };
    modalRef.componentInstance.isCommunityPost = true;
    modalRef.componentInstance.isBetaComment = this.betaStringId ? true : false;
    modalRef.result.then(
      (result) => {
        if (result) {
          // this.post.message = result.message;
          if (result.message) editingComment.message = result.message;
          editingComment.htmlMessage = this.appService.urlify(result.message);
          let editedData: any = {
            message: editingComment.message,
            edited: true,
          };
          if (result.subject) {
            editedData.subject = result.subject;
          }
          if (result.scheduled_date) {
            editedData.scheduled_date = result.scheduled_date;
          }
          if (result.created) {
            editedData.created = result.created;
          }
          if (result.edits) {
            editedData.edits = result.edits.length ? result.edits : null;
          }
          if (result.mentions) {
            editedData.mentions = result.mentions.length ? result.mentions : null;
          }
          if (result.channels) {
            editedData.channels = result.channels.length ? result.channels : null;
          }
          if (result.files) {
            editedData.files = result.files;
          }

          if (result.articles) {
            editedData.articles = result.articles.length ? result.articles : null;
          }
          if (result.learn_content) {
            editedData.learn_content = result.learn_content.length ? result.learn_content : null;
          }
          if (comment.message != editedData.message) {
            comment.message = editedData.message;
            comment = this.communityService.buildHtmlComment(comment);
          }
          this.communityService.updatePost(editingComment.key || editingComment.id, editedData, this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection).then(() => {
            if (isPinnedBetaPost) this._eventbusService.emit(this._eventbusService.types.betaAnnouncementUpdated, true);
          });
          this.postEdited.next({ post: editingComment, index: this.index });
        }
      },
      (error) => {}
    );
  }

  fileOverBase(e) {
    this.hasBaseDropZoneOver = e;
  }

  attachFile(droppedFile = false) {
    const modalRef = this.appService.showModal(WorkspaceUploadComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.isPostAttachment = true;
    if (droppedFile) modalRef.componentInstance.droppedFile = droppedFile;
    modalRef.componentInstance.uploadProgressChanged.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.uploadingFiles.forEach((item) => {
        if (item.id == result.file.id) item.progress = result.progress;
      });
    });
    modalRef.componentInstance.fileCreated.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!this.uploadingFiles) this.uploadingFiles = [];
      this.uploadingFiles.push(result);
    });
    modalRef.componentInstance.fileUploadFailed.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.uploadingFiles = this.uploadingFiles.filter((item) => item.id != result.id);
    });
    modalRef.componentInstance.fileUploadFinished.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this._zone.run(() => {
        if (!this.uploadedAttachments) this.uploadedAttachments = [];
        result.url = this.communityService.buildFileUrl(result.user_id, result);
        this.uploadedAttachments.push(result);
        this.uploadingFiles = this.uploadingFiles.filter((item) => item.id != result.id);
      });
    });
  }

  removeAttachment(attachment?) {
    if (attachment) {
      this.uploadedAttachments = this.uploadedAttachments.filter((item) => item.id != attachment.id);
    } else {
      this.uploadedAttachments = undefined;
      this.uploadingFiles = undefined;
    }
  }

  /**
   * Narrow-screen flag driving the tooltip trigger mode (tap-to-open on mobile, hover on
   * desktop). A plain property rather than a method because template bindings re-evaluate
   * on every change-detection pass and this reads layout off `window` — with a feed full
   * of posts that adds up fast. Refreshed on resize instead.
   */
  public isMobileScreen = typeof window !== "undefined" && window.innerWidth < 768;

  @HostListener("window:resize")
  onWindowResize() {
    this.isMobileScreen = typeof window !== "undefined" && window.innerWidth < 768;
  }

  ngOnDestroy() {
    clearTimeout(this._linkCopiedTimer);
  }
}
